
import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Image,
  Nav,
  Navbar,
  Row,
  Stack,
} from "react-bootstrap";

/**
 * Easy Button Insurance — Landing Page v01 (React + TypeScript + react-bootstrap)
 * ---------------------------------------------------------------------------------
 * Created: 2025-08-23
 * Author: ChatGPT (GPT-5 Thinking)
 *
 * Usage:
 * 1) Ensure bootstrap CSS is included once in your app entry (index.tsx or App.tsx):
 *    import "bootstrap/dist/css/bootstrap.min.css";
 * 2) Drop this component anywhere (e.g., <EasyButtonInsuranceLanding />) — it's self‑contained.
 * 3) Replace placeholder logo, images, and contact details with your own.
 *
 * Notes:
 * - Uses react-bootstrap layout/components for a responsive, clean landing page.
 * - Includes a simple quote form with client-side validation and a fake submit handler.
 * - All sections are modular so you can reorder/remove easily.
 */

// ---- Types
interface QuotePayload {
  fullName: string;
  email: string;
  phone?: string;
  state?: string;
  coverageType: "Auto" | "Home" | "Renters" | "Business" | "Umbrella" | "Other";
  notes?: string;
  consent: boolean;
}

// ---- Utilities
const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DC","DE","FL","GA","HI","IA","ID","IL","IN","KS","KY","LA","MA","MD","ME","MI","MN","MO","MS","MT","NC","ND","NE","NH","NJ","NM","NV","NY","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VA","VT","WA","WI","WV","WY",
];

const COVERAGE_OPTIONS: QuotePayload["coverageType"][] = [
  "Auto","Home","Renters","Business","Umbrella","Other",
];

// Simple email + phone validators
const isEmail = (v: string) => /.+@.+\..+/.test(v);
const isPhone = (v: string) => /[0-9\-\+()\.\s]{7,}/.test(v);

// ---- Component
export const EasyButtonInsuranceLandingPage_v01: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<QuotePayload>({
    fullName: "",
    email: "",
    phone: "",
    state: "CO",
    coverageType: "Auto",
    notes: "",
    consent: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const phoneDisplay = useMemo(() => "(303) 555‑0100", []);

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.fullName.trim()) e.fullName = "Please enter your full name.";
    if (!isEmail(form.email)) e.email = "Enter a valid email address.";
    if (form.phone && !isPhone(form.phone)) e.phone = "Optional, but must look like a phone number.";
    if (!form.consent) e.consent = "Please accept the consent to proceed.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;
    try {
      setSubmitting(true);
      // Simulated network call — replace with your API Gateway/Fetch
      await new Promise((r) => setTimeout(r, 850));
      console.log("Quote request →", form);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const onChange = (patch: Partial<QuotePayload>) => setForm((f) => ({ ...f, ...patch }));

  return (
    <div className="bg-light">
      {/* NAVBAR */}
      <Navbar bg="white" expand="lg" sticky="top" className="border-bottom shadow-sm-sm">
        <Container>
          <Navbar.Brand href="#home" className="d-flex align-items-center gap-2">
            <div
              aria-label="Easy Button Insurance logo"
              className="rounded-circle d-inline-flex align-items-center justify-content-center"
              style={{ width: 36, height: 36, background: "#0d6efd", color: "white", fontWeight: 700 }}
            >
              EB
            </div>
            <span className="fw-bold">easy button insurance</span>
            <Badge bg="success" pill className="ms-2">New</Badge>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="main-nav" />
          <Navbar.Collapse id="main-nav">
            <Nav className="ms-auto align-items-lg-center gap-2">
              <Nav.Link href="#benefits">Benefits</Nav.Link>
              <Nav.Link href="#how">How it works</Nav.Link>
              <Nav.Link href="#faq">FAQ</Nav.Link>
              <Button href="#quote" variant="primary">Get a Quote</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO */}
      <header className="py-5 py-lg-6 bg-white border-bottom">
        <Container>
          <Row className="align-items-center g-4">
            <Col lg={6}>
              <h1 className="display-5 fw-bold lh-tight">
                Insurance that feels like pressing the <span className="text-primary">easy button</span>.
              </h1>
              <p className="lead text-secondary mt-3">
                Compare top carriers in minutes. One short form. Instant guidance. No pushy sales.
              </p>
              <Stack direction="horizontal" gap={2} className="mt-3">
                <Button size="lg" variant="primary" href="#quote">Get my price</Button>
                <Button size="lg" variant="outline-secondary" href="#how">See how it works</Button>
              </Stack>
              <div className="text-muted mt-3 small">
                <span className="me-3">Call us: <a href={`tel:${phoneDisplay}`} className="text-decoration-none">{phoneDisplay}</a></span>
                <span className="me-2">Licensed in 40+ states</span>
                <Badge bg="info" className="align-middle">A+ Rated Partners</Badge>
              </div>
            </Col>
            <Col lg={6} className="text-center">
              <Card className="border-0 shadow-lg mx-auto" style={{ maxWidth: 520 }}>
                <Card.Body className="p-4 p-md-5">
                  <div className="d-flex align-items-center justify-content-center mb-3">
                    <span className="rounded-circle bg-primary bg-opacity-10 p-3 me-2">
                      <span className="bi bi-lightning-charge-fill text-primary" aria-hidden></span>
                    </span>
                    <h2 className="h4 m-0">Start your quote</h2>
                  </div>
                  {submitted ? (
                    <Alert variant="success" className="mb-0">
                      <h3 className="h5">You're all set 🎉</h3>
                      <p className="mb-0">Thanks! An agent will email your options shortly. Check your inbox.</p>
                    </Alert>
                  ) : (
                    <Form onSubmit={onSubmit} noValidate>
                      <Row className="g-3">
                        <Col xs={12}>
                          <Form.Label htmlFor="fullName">Full name</Form.Label>
                          <Form.Control
                            id="fullName"
                            placeholder="Jane Smith"
                            value={form.fullName}
                            onChange={(e) => onChange({ fullName: e.target.value })}
                            isInvalid={!!errors.fullName}
                          />
                          <Form.Control.Feedback type="invalid">{errors.fullName}</Form.Control.Feedback>
                        </Col>
                        <Col md={6}>
                          <Form.Label htmlFor="email">Email</Form.Label>
                          <Form.Control
                            id="email"
                            type="email"
                            placeholder="you@example.com"
                            value={form.email}
                            onChange={(e) => onChange({ email: e.target.value })}
                            isInvalid={!!errors.email}
                          />
                          <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                        </Col>
                        <Col md={6}>
                          <Form.Label htmlFor="phone">Phone (optional)</Form.Label>
                          <Form.Control
                            id="phone"
                            placeholder="(555) 123‑4567"
                            value={form.phone}
                            onChange={(e) => onChange({ phone: e.target.value })}
                            isInvalid={!!errors.phone}
                          />
                          <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
                        </Col>
                        <Col md={6}>
                          <Form.Label htmlFor="state">State</Form.Label>
                          <Form.Select
                            id="state"
                            value={form.state}
                            onChange={(e) => onChange({ state: e.target.value })}
                          >
                            {STATES.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col md={6}>
                          <Form.Label htmlFor="coverage">Coverage type</Form.Label>
                          <Form.Select
                            id="coverage"
                            value={form.coverageType}
                            onChange={(e) => onChange({ coverageType: e.target.value as QuotePayload["coverageType"] })}
                          >
                            {COVERAGE_OPTIONS.map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col xs={12}>
                          <Form.Label htmlFor="notes">Anything we should know? (optional)</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            id="notes"
                            placeholder="E.g., current carrier, renewal date, special coverage needs..."
                            value={form.notes}
                            onChange={(e) => onChange({ notes: e.target.value })}
                          />
                        </Col>
                        <Col xs={12}>
                          <Form.Check
                            id="consent"
                            checked={form.consent}
                            onChange={(e) => onChange({ consent: e.target.checked })}
                            isInvalid={!!errors.consent}
                            feedback={errors.consent}
                            feedbackType="invalid"
                            label={
                              <span>
                                I agree to be contacted by Easy Button Insurance about my quote.
                                <span className="text-muted"> (Email/SMS; no spam)</span>
                              </span>
                            }
                          />
                        </Col>
                        <Col xs={12} className="d-grid">
                          <Button type="submit" size="lg" disabled={submitting}>
                            {submitting ? "Submitting..." : "Get my free quote"}
                          </Button>
                        </Col>
                        <Col xs={12}>
                          <div className="text-center small text-muted">
                            By submitting, you agree to our <a href="#" className="text-decoration-none">Terms</a> and <a href="#" className="text-decoration-none">Privacy Policy</a>.
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </header>

      {/* TRUST + BENEFITS */}
      <section className="py-5" id="benefits">
        <Container>
          <Row className="g-4">
            {[{
              title: "One simple form",
              text: "No repetitive questions. We map your answers across multiple carriers.",
            },{
              title: "Real savings, fast",
              text: "See competitive options in minutes, often saving $400+/yr.",
            },{
              title: "Human help on demand",
              text: "Licensed agents available by phone, chat, or email.",
            }].map((b, i) => (
              <Col md={4} key={i}>
                <Card className="h-100 shadow-sm">
                  <Card.Body>
                    <div className="d-flex align-items-center mb-2">
                      <span className="badge bg-primary me-2">{i + 1}</span>
                      <h3 className="h5 m-0">{b.title}</h3>
                    </div>
                    <p className="mb-0 text-secondary">{b.text}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-5 bg-white border-top border-bottom" id="how">
        <Container>
          <Row className="g-4 align-items-center">
            <Col lg={6}>
              <h2 className="h1 fw-bold mb-3">How it works</h2>
              <ol className="mb-4 lead">
                <li>Tell us about yourself and your coverage needs.</li>
                <li>We compare top carriers and find your best fit.</li>
                <li>Review options, pick a plan, and press the easy button.</li>
              </ol>
              <Stack direction="horizontal" gap={3} className="flex-wrap">
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-success">A+</span>
                  <span className="text-muted">Top-rated partners</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-secondary">SSL</span>
                  <span className="text-muted">Bank-level security</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-info text-dark">24/7</span>
                  <span className="text-muted">Anytime quotes</span>
                </div>
              </Stack>
            </Col>
            <Col lg={6}>
              <Card className="shadow-sm">
                <Card.Body>
                  <Row className="g-3">
                    {[{
                      step: 1,
                      title: "Answer a few questions",
                      text: "Takes 60–90 seconds. No credit impact.",
                    },{
                      step: 2,
                      title: "Compare your options",
                      text: "Transparent pricing with apples-to-apples coverage.",
                    },{
                      step: 3,
                      title: "Activate your policy",
                      text: "Instant digital ID cards. Humans when you want them.",
                    }].map((s) => (
                      <Col sm={4} key={s.step}>
                        <div className="text-center p-3 border rounded h-100">
                          <div className="display-6 fw-bold text-primary">{s.step}</div>
                          <div className="fw-semibold mt-2">{s.title}</div>
                          <div className="small text-secondary mt-1">{s.text}</div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-5">
        <Container>
          <h2 className="h1 fw-bold text-center mb-4">Loved by smart shoppers</h2>
          <Row className="g-4">
            {[
              {
                name: "Alex R.",
                quote: "Easiest quote I've ever done. Saved me $38/month and took five minutes.",
              },
              {
                name: "Priya K.",
                quote: "Agent walked me through coverages without jargon. Actually helpful!",
              },
              {
                name: "Marco D.",
                quote: "Switched home + auto and bundled. Smooth experience and solid price.",
              },
            ].map((t, i) => (
              <Col md={4} key={i}>
                <Card className="h-100 shadow-sm">
                  <Card.Body>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span className="badge bg-warning text-dark">★★★★★</span>
                      <span className="small text-muted">Verified buyer</span>
                    </div>
                    <blockquote className="mb-3">“{t.quote}”</blockquote>
                    <div className="fw-semibold">{t.name}</div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* FAQ */}
      <section className="py-5 bg-white border-top border-bottom" id="faq">
        <Container>
          <Row className="g-4">
            <Col lg={6}>
              <h2 className="h1 fw-bold">Frequently asked</h2>
              <p className="lead text-secondary">Short, no‑fluff answers to help you decide faster.</p>
            </Col>
            <Col lg={6}>
              <div className="accordion" id="faqAcc">
                {[
                  {
                    q: "Will getting a quote affect my credit score?",
                    a: "No. Our quote process does not impact your credit score.",
                  },
                  {
                    q: "How long does it take to see options?",
                    a: "Most shoppers see recommended options within minutes after submitting the form.",
                  },
                  {
                    q: "Do you sell my data?",
                    a: "Never. We only use your information to provide quotes and support your policy.",
                  },
                ].map((item, idx) => (
                  <div className="accordion-item" key={idx}>
                    <h2 className="accordion-header" id={`faq-h-${idx}`}>
                      <button
                        className={`accordion-button ${idx === 0 ? "" : "collapsed"}`}
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#faq-c-${idx}`}
                        aria-expanded={idx === 0}
                        aria-controls={`faq-c-${idx}`}
                      >
                        {item.q}
                      </button>
                    </h2>
                    <div
                      id={`faq-c-${idx}`}
                      className={`accordion-collapse collapse ${idx === 0 ? "show" : ""}`}
                      aria-labelledby={`faq-h-${idx}`}
                      data-bs-parent="#faqAcc"
                    >
                      <div className="accordion-body">{item.a}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA */}
      <section className="py-5" id="quote">
        <Container>
          <Card className="p-4 p-md-5 shadow-sm bg-primary text-white">
            <Row className="align-items-center g-4">
              <Col md={8}>
                <h2 className="display-6 fw-bold mb-2">Ready to press the easy button?</h2>
                <p className="lead mb-0">Start a quote now and see your best options in minutes.</p>
              </Col>
              <Col md={4} className="text-md-end">
                <Button size="lg" variant="light" href="#home">Start my quote</Button>
              </Col>
            </Row>
          </Card>
        </Container>
      </section>

      {/* FOOTER */}
      <footer className="py-4 bg-white border-top">
        <Container>
          <Row className="g-3 align-items-center">
            <Col md={6}>
              <div className="d-flex align-items-center gap-2">
                <div
                  className="rounded-circle d-inline-flex align-items-center justify-content-center"
                  style={{ width: 28, height: 28, background: "#0d6efd", color: "white", fontWeight: 700 }}
                >
                  EB
                </div>
                <span className="fw-semibold">easy button insurance</span>
              </div>
              <div className="small text-muted mt-2">© {new Date().getFullYear()} Easy Button Insurance. All rights reserved.</div>
            </Col>
            <Col md={6} className="text-md-end small">
              <a href="#" className="text-decoration-none me-3">Licensing</a>
              <a href="#" className="text-decoration-none me-3">Privacy</a>
              <a href="#" className="text-decoration-none">Terms</a>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

export default EasyButtonInsuranceLandingPage_v01;
